import React, { useEffect, useMemo } from 'react';
import { Button, LoadingSpinner } from 'common.ui';
import { AuthService } from 'auth/AuthService';
import { useHistory, useLocation } from 'react-router-dom';
import RoutePaths from 'RoutePaths';
import { useTranslation } from 'react-i18next';
import { useMsal } from '../../auth/MsalContext';
import Page from '../../components/page/Page';

function PasswordReset() {
  const { resetPassword } = useMsal();
  const { t } = useTranslation('pages', { keyPrefix: 'passwordReset.passwordReset' });
  const history = useHistory();
  const location = useLocation();

  const parsedQueryString = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // Fragmentet starter med #, som URLSearchParams ikke fjerner selv.
  const parsedHashString = useMemo(() => new URLSearchParams(location.hash.replace(/^#/, '')), [location.hash]);
  const b2cErrorCode = 'AADB2C90091';

  // Utfallet av flyten utledes av url-en i stedet for å lagres i state fra en effekt
  const queryError = parsedQueryString.get('error');
  const hashError = parsedHashString.get('error');
  const isAutoForward = !!parsedQueryString.get('autoForward');
  const isPasswordResetFlowSuccessful = !isAutoForward && !queryError && !hashError;
  const isPasswordResetFlowCancelled =
    !isAutoForward &&
    !isPasswordResetFlowSuccessful &&
    !!(
      (queryError && parsedQueryString.get('error_description')?.indexOf(b2cErrorCode) !== -1) ||
      (hashError && parsedHashString.get('error_description')?.indexOf(b2cErrorCode) !== -1)
    );
  const hasUnhandledError =
    !isAutoForward && !isPasswordResetFlowSuccessful && !isPasswordResetFlowCancelled && !!(queryError || hashError);

  useEffect(() => {
    if (isAutoForward) {
      resetPassword(parsedQueryString.get('ui_locales') || undefined);
      return;
    }

    if (hasUnhandledError) {
      console.error(`Password reset error: ${queryError || hashError}`);
      history.push(RoutePaths.home);
    }
  }, [history, isAutoForward, hasUnhandledError, parsedQueryString, queryError, hashError, resetPassword]);

  const message = !isPasswordResetFlowCancelled ? t('message.completed') : t('message.cancelled');

  const onButtonClick = async () => {
    if (isPasswordResetFlowCancelled) {
      AuthService.clearStorageValues();
      resetPassword();
      return;
    }

    // Passordet er endret: logg brukeren helt ut, også av B2C-sesjonen, slik at neste
    // innlogging krever det nye passordet. Å bare tømme MSAL-cachen lokalt er ikke nok -
    // SSO-cookien lever videre og logger brukeren rett inn igjen uten passord.
    await AuthService.signOut(RoutePaths.postLogout);
  };

  return (
    <Page header={t('title')} title={t('title')}>
      {isPasswordResetFlowSuccessful || isPasswordResetFlowCancelled ? (
        <p>
          <br />
          {message}
          <br />
          <br />
          <Button
            type='button'
            onClick={onButtonClick}
            text={isPasswordResetFlowCancelled ? t('btnText.changePassword') : t('btnText.continue')}
            styleType='light'
          />
        </p>
      ) : (
        <LoadingSpinner />
      )}
    </Page>
  );
}

export default PasswordReset;
